/**
 * SelfCheckInCard — Self-service manual Check-in / Check-out for the
 * logged-in staff member (Iter-66, message #597).
 *
 * Shows a prominent card at the top of the Attendance page with:
 *   • "Check In" button when the user has no attendance row for today.
 *   • "Check Out" button + time_in stamp when they're checked-in but
 *     have not yet checked out.
 *   • Closed-day summary (In / Out / worked duration) when they've
 *     already checked out.
 *
 * Uses the existing `/api/attendance/check-in` and
 * `/api/attendance/check-out/{id}` endpoints — the same paths the
 * admin per-row buttons use — so a self check-in and an admin
 * check-in produce identical attendance rows. Server infers the
 * business-hours status on check-out (Late / Half-Day / Early Exit /
 * Overtime) via the shared iter-58 recompute pass.
 *
 * Hidden for Parents, Guardians and Students — they don't have staff
 * attendance rows. Also hidden when no specific branch is selected
 * (activeBranch === "all") because we need a concrete branch_id.
 */
import { useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { fmtTime } from "@/lib/helpers";
import { SignIn, SignOut, Clock, CheckCircle } from "@phosphor-icons/react";

function fmtWorked(inISO, outISO) {
  if (!inISO || !outISO) return null;
  const ms = new Date(outISO) - new Date(inISO);
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const mins = Math.floor(ms / 60000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

export default function SelfCheckInCard({ user, activeBranch, attendance, onChanged }) {
  const [busy, setBusy] = useState(false);

  const roles = useMemo(() => new Set((user?.roles || []).map(r => String(r).toLowerCase())), [user]);
  const isStaff = user && !(roles.has("parent") || roles.has("guardian"));
  const isTherapist = roles.has("therapist");

  // Find TODAY's attendance row for the current user across all
  // subject types (therapist / staff). The `attendance` prop is the
  // list already loaded by AttendancePage for the active branch.
  const myAtt = useMemo(() => {
    if (!user) return null;
    return (attendance || []).find(
      (a) => a.subject_id === user.id
        && (a.subject_type === "therapist" || a.subject_type === "staff"),
    ) || null;
  }, [attendance, user]);

  if (!isStaff) return null;
  if (!activeBranch || activeBranch === "all") {
    return (
      <Card className="border-dashed" data-testid="self-checkin-card">
        <CardContent className="p-4 flex items-center gap-3 text-sm">
          <Avatar className="h-10 w-10">
            <AvatarFallback className="bg-primary/10 text-primary font-semibold">
              {(user?.name || "?").slice(0, 1).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <div className="font-medium">Your check-in</div>
            <div className="text-xs text-muted-foreground">
              Pick a specific branch to check yourself in for today.
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const checkIn = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/attendance/check-in", {
        subject_id: user.id,
        subject_type: isTherapist ? "therapist" : "staff",
        branch_id: activeBranch,
        status: "present",
      });
      toast.success("Checked in");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const checkOut = async () => {
    if (busy || !myAtt) return;
    setBusy(true);
    try {
      await api.post(`/attendance/check-out/${myAtt.id}`);
      toast.success("Checked out");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const notCheckedIn = !myAtt;
  const inProgress = myAtt && myAtt.time_in && !myAtt.time_out;
  const closed = myAtt && myAtt.time_out;
  const worked = closed ? fmtWorked(myAtt.time_in, myAtt.time_out) : null;

  return (
    <Card
      className={
        inProgress
          ? "border-emerald-200 bg-emerald-50/50"
          : closed
            ? "border-slate-200 bg-muted/30"
            : "border-primary/30 bg-primary/[0.03]"
      }
      data-testid="self-checkin-card"
    >
      <CardContent className="p-4 flex flex-wrap items-center gap-3">
        <Avatar className="h-11 w-11 shrink-0">
          <AvatarFallback className="bg-primary/10 text-primary font-semibold">
            {(user?.name || "?").slice(0, 1).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-[160px]">
          <div className="text-sm font-medium truncate flex items-center gap-2 flex-wrap">
            <span>Your attendance today</span>
            {inProgress && (
              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200" data-testid="self-status-in">
                <Clock size={11} className="mr-1" weight="duotone" /> Checked in
              </Badge>
            )}
            {closed && (
              <Badge variant="outline" className="text-muted-foreground" data-testid="self-status-out">
                <CheckCircle size={11} className="mr-1" weight="duotone" /> Checked out
              </Badge>
            )}
          </div>
          <div className="text-xs text-muted-foreground truncate">
            {notCheckedIn && "You have not checked in yet today."}
            {inProgress && (
              <>In at <span className="font-medium text-foreground">{fmtTime(myAtt.time_in)}</span></>
            )}
            {closed && (
              <>
                In <span className="font-medium text-foreground">{fmtTime(myAtt.time_in)}</span>
                {" · "}
                Out <span className="font-medium text-foreground">{fmtTime(myAtt.time_out)}</span>
                {worked && <> · {worked} worked</>}
              </>
            )}
          </div>
        </div>

        {notCheckedIn && (
          <Button
            size="sm"
            onClick={checkIn}
            disabled={busy}
            className="gap-1.5"
            data-testid="self-checkin-btn"
          >
            <SignIn size={14} /> Check In
          </Button>
        )}
        {inProgress && (
          <Button
            size="sm"
            variant="outline"
            onClick={checkOut}
            disabled={busy}
            className="gap-1.5"
            data-testid="self-checkout-btn"
          >
            <SignOut size={14} /> Check Out
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
